import { useEffect, useRef, useState } from 'react';
import { toApiError } from '../api/client';
import { billingApi, organisationApi } from '../api/endpoints';
import type { MultiBranchStatus, Payment, PaymentOrder } from '../api/types';
import { date, money } from '../utils/format';
import { openRazorpayCheckout, type RazorpayCheckoutResponse } from '../utils/razorpay-checkout';
import { IconAlert, IconCard, IconCheck, IconClock } from './Icons';
import { InlineError } from './States';

/**
 * Buying the organisation's yearly multi-branch entitlement.
 *
 * Deliberately separate from {@link Checkout}, which sells a branch's own subscription or
 * a single listing fee. That one picks a plan and applies to one property; this charge
 * belongs to the organisation, governs no branch, and has nothing to choose - so there is
 * no plan step at all.
 *
 * It never computes a price: every figure shown comes from the status the server returned
 * or the order it created. It never marks anything paid: the receipt appears only after
 * the API returns a confirmed payment. And it never decides which payment method is on
 * offer - `status.simulatedCheckoutAvailable` and `status.gatewayIntegrated` both come
 * from the server's own environment and provider, so this cannot offer a button the API
 * would refuse.
 */

type Stage = 'terms' | 'summary' | 'pending' | 'done';

export function MultiBranchCheckout({
  businessId,
  organisationName,
  status,
  onActivated,
  onCancel,
}: {
  businessId: string;
  organisationName?: string;
  status: MultiBranchStatus;
  /** Called after a confirmed payment, so the caller can re-read server state. */
  onActivated: () => Promise<void> | void;
  onCancel: () => void;
}) {
  const [stage, setStage] = useState<Stage>('terms');
  const [order, setOrder] = useState<PaymentOrder | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);

  // The server is the only thing that may say a simulated confirmation is possible.
  const canSimulate = status.simulatedCheckoutAvailable;

  // ...and the only thing that may say a real provider can carry this to settlement. Without
  // this, a server configured for Razorpay offered no way at all to buy the entitlement.
  const canUseRazorpay = status.gatewayIntegrated && order?.provider === 'Razorpay';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Escape is disabled mid-payment: closing between a captured payment and the
      // receipt would leave the owner unsure whether they had been charged.
      if (event.key === 'Escape' && !busy) onCancel();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  const createOrder = async () => {
    if (busy) return; // what stops a double click opening two orders
    setBusy(true);
    setError(null);

    try {
      setOrder(await organisationApi.createMultiBranchOrder(businessId));
      setStage('summary');
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmSimulated = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      // Idempotent server-side: confirming an already-paid payment returns the same
      // result rather than granting a second entitlement period.
      const result = await billingApi.simulatePayment(order.paymentId, true);
      setPayment(result);

      if (result.status === 'Paid') {
        setStage('done');
      } else {
        setError(`The payment did not complete. Its status is ${result.status}.`);
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-reads the organisation's payments, so the receipt is the server's own record rather
   * than anything the checkout widget handed back.
   */
  const findPayment = async (paymentId: string): Promise<Payment | undefined> => {
    const payments = await billingApi.payments({ businessId, page: 1, pageSize: 50 });
    return payments.items.find((item) => item.id === paymentId);
  };

  /**
   * Associates a completed Razorpay checkout with the order that was stored when it was
   * created. A verified checkout response is not treated as settlement on its own: the
   * receipt appears only once the API itself reports the payment Paid.
   */
  const confirmRazorpay = async (result: RazorpayCheckoutResponse) => {
    if (!order) return;
    setBusy(true);
    setError(null);

    try {
      const confirmation = await billingApi.confirmCheckout({
        paymentId: order.paymentId,
        providerOrderId: result.razorpay_order_id,
        providerPaymentId: result.razorpay_payment_id,
        signature: result.razorpay_signature,
      });

      if (confirmation.status === 'Paid') {
        const confirmed = await findPayment(order.paymentId);

        if (confirmed?.status === 'Paid') {
          setPayment(confirmed);
          setStage('done');
        } else {
          setStage('pending');
          setError(
            'Razorpay confirmed the payment, but the server is still updating the receipt. Check the status again shortly.',
          );
        }
      } else {
        setStage('pending');
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const startRazorpayCheckout = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      await openRazorpayCheckout(order.checkoutPayload, {
        onSuccess: (result) => {
          void confirmRazorpay(result);
        },
        onFailure: (message) => {
          setError(message);
          setBusy(false);
        },
        onDismiss: () => setBusy(false),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Razorpay Checkout could not be opened.');
      setBusy(false);
    }
  };

  /** For an order left pending while the verified provider notification catches up. */
  const checkPaymentStatus = async () => {
    if (!order || busy) return;
    setBusy(true);
    setError(null);

    try {
      const latest = await findPayment(order.paymentId);

      if (latest?.status === 'Paid') {
        setPayment(latest);
        setStage('done');
      } else if (latest?.status === 'Failed') {
        setError('Razorpay reported that this payment failed. You can try again from the order summary.');
        setStage('summary');
      } else {
        setError(
          'Payment is still awaiting Razorpay confirmation. Check again shortly; do not create another order.',
        );
      }
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  /** Dismisses the receipt and only then lets the caller continue. */
  const finish = async () => {
    if (busy) return;
    setBusy(true);

    try {
      await onActivated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div
        className="modal d-block"
        role="dialog"
        aria-modal="true"
        aria-labelledby="multibranch-title"
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content" ref={dialogRef}>
            <div className="modal-header">
              <h2 className="modal-title h5" id="multibranch-title">
                {stage === 'done' ? 'Subscription active' : 'Multi-branch subscription'}
              </h2>
            </div>

            <div className="modal-body">
              <InlineError message={error} />

              {stage === 'terms' && <Terms status={status} canSimulate={canSimulate} />}

              {stage === 'summary' && order && (
                <Summary
                  order={order}
                  status={status}
                  organisationName={organisationName}
                  canUseRazorpay={canUseRazorpay}
                />
              )}

              {stage === 'pending' && <PendingNotice />}

              {stage === 'done' && payment && (
                <Receipt payment={payment} organisationName={organisationName} />
              )}
            </div>

            <div className="modal-footer">
              {stage === 'terms' && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onCancel}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={createOrder}
                    disabled={busy}
                  >
                    <IconCard /> {busy ? 'Preparing…' : 'Proceed to pay'}
                  </button>
                </>
              )}

              {stage === 'summary' && order && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setOrder(null);
                      setStage('terms');
                    }}
                    disabled={busy}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={order.isSimulated ? confirmSimulated : startRazorpayCheckout}
                    disabled={busy || (order.isSimulated ? !canSimulate : !canUseRazorpay)}
                  >
                    {busy
                      ? order.isSimulated
                        ? 'Confirming…'
                        : 'Opening secure checkout…'
                      : order.isSimulated
                        ? `Confirm and pay ${money(order.totalAmount)}`
                        : `Pay with Razorpay Test Mode ${money(order.totalAmount)}`}
                  </button>
                </>
              )}

              {stage === 'pending' && order && (
                <>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setStage('summary')}
                    disabled={busy}
                  >
                    Back to order
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={checkPaymentStatus}
                    disabled={busy}
                  >
                    {busy ? 'Checking…' : 'Check payment status'}
                  </button>
                </>
              )}

              {stage === 'done' && (
                <button type="button" className="btn btn-primary" onClick={finish} disabled={busy}>
                  {busy ? 'Continuing…' : 'Continue'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Terms({ status, canSimulate }: { status: MultiBranchStatus; canSimulate: boolean }) {
  return (
    <>
      {/* The server's own wording, rendered as given rather than reworded here. */}
      <p className="mb-3">{status.message}</p>

      <dl className="sf-kv mb-3">
        <div>
          <dt>Service</dt>
          <dd>Yearly multi-branch subscription</dd>
        </div>
        <div>
          <dt>Base amount</dt>
          <dd>{money(status.amount)}</dd>
        </div>
        <div>
          <dt>Tax ({status.taxPercentage}%)</dt>
          <dd>{money(status.totalAmount - status.amount)}</dd>
        </div>
        <div>
          <dt>Valid for</dt>
          <dd>{status.durationDays} days</dd>
        </div>
        <div className="sf-kv__total">
          <dt>Total payable</dt>
          <dd>
            <strong>{money(status.totalAmount)}</strong>
          </dd>
        </div>
      </dl>

      <h3 className="sf-section-title">Payment method</h3>

      {canSimulate ? (
        <div className="sf-paymethod is-selected">
          <IconAlert className="sf-paymethod__icon" />
          <span>
            <strong>Development Test Payment</strong>
            <span className="sf-muted d-block small">
              No real money will be charged. Recorded as a simulated payment.
            </span>
          </span>
        </div>
      ) : status.gatewayIntegrated ? (
        <div className="sf-paymethod is-selected">
          <IconCard className="sf-paymethod__icon" />
          <span>
            <strong>Razorpay Test Mode</strong>
            <span className="sf-muted d-block small">
              Secure sandbox checkout. No real money will be charged.
            </span>
          </span>
        </div>
      ) : (
        <div className="sf-paymethod">
          <IconClock className="sf-paymethod__icon" />
          <span>
            <strong>No payment method available</strong>
            <span className="sf-muted d-block small">
              The order can be recorded now and paid once a live provider is configured.
            </span>
          </span>
        </div>
      )}

      <p className="sf-muted small mb-0 mt-3">
        This covers the organisation, not a single branch. Each branch still carries its own
        monthly subscription.
      </p>
    </>
  );
}

function Summary({
  order,
  status,
  organisationName,
  canUseRazorpay,
}: {
  order: PaymentOrder;
  status: MultiBranchStatus;
  organisationName?: string;
  canUseRazorpay: boolean;
}) {
  return (
    <>
      <h3 className="sf-section-title">Confirm your order</h3>

      <dl className="sf-kv mb-3">
        <div>
          <dt>Service</dt>
          <dd>Yearly multi-branch subscription</dd>
        </div>
        {organisationName && (
          <div>
            <dt>Applies to</dt>
            <dd>{organisationName}</dd>
          </div>
        )}
        <div>
          <dt>Base amount</dt>
          <dd>{money(order.amount)}</dd>
        </div>
        <div>
          <dt>Tax ({status.taxPercentage}%)</dt>
          <dd>{money(order.taxAmount)}</dd>
        </div>
        <div>
          <dt>Duration</dt>
          <dd>{status.durationDays} days</dd>
        </div>
        <div className="sf-kv__total">
          <dt>Total payable</dt>
          <dd>
            <strong>{money(order.totalAmount)}</strong>
          </dd>
        </div>
      </dl>

      {order.isSimulated && (
        <div className="sf-simulated-banner mb-0" role="note">
          <strong>Development test payment.</strong> Confirming below records a simulated payment
          and activates multi-branch for testing. No real money will be charged.
        </div>
      )}

      {!order.isSimulated && order.provider === 'Razorpay' && (
        <div className="sf-simulated-banner mb-0" role="note">
          <strong>Razorpay Test Mode.</strong> This is a sandbox payment; no real money will be
          charged.
        </div>
      )}

      {!order.isSimulated && !canUseRazorpay && (
        <p className="sf-muted small mb-0 mt-2">
          This order cannot be completed because the server has not enabled a matching payment
          method.
        </p>
      )}
    </>
  );
}

/**
 * Shown when a checkout returned but the entitlement is not active yet. The verified
 * provider notification is the authority, so the owner is told to wait rather than pay again.
 */
function PendingNotice() {
  return (
    <>
      <h3 className="sf-section-title">Payment confirmation pending</h3>
      <p className="mb-0">
        Razorpay returned a payment response. RoomKart is waiting for the verified provider
        notification before activating multi-branch access. Do not pay again while this order is
        pending.
      </p>
    </>
  );
}

function Receipt({ payment, organisationName }: { payment: Payment; organisationName?: string }) {
  return (
    <>
      <div className="sf-checkout__done">
        <span className="sf-checkout__tick" aria-hidden="true">
          <IconCheck />
        </span>
        <h3 className="h5 mb-1">
          {payment.isSimulated ? 'Development payment successful' : 'Payment successful'}
        </h3>
        <p className="sf-muted mb-0">
          {organisationName ? `${organisationName} can now run more than one branch.` : 'Your organisation can now run more than one branch.'}
        </p>
      </div>

      {payment.isSimulated && (
        <div className="sf-simulated-banner my-3" role="note">
          <strong>No money was charged.</strong> This payment was completed by the development
          simulator and is recorded as simulated. It is not a real financial transaction.
        </div>
      )}

      <dl className="sf-kv mb-0">
        <div>
          <dt>Reference</dt>
          <dd className="font-monospace">{payment.id}</dd>
        </div>
        <div>
          <dt>Amount</dt>
          <dd>{money(payment.amount)}</dd>
        </div>
        <div>
          <dt>Tax</dt>
          <dd>{money(payment.taxAmount)}</dd>
        </div>
        <div>
          <dt>Total paid</dt>
          <dd>
            <strong>{money(payment.totalAmount)}</strong>
          </dd>
        </div>
        {payment.paidAtUtc && (
          <div>
            <dt>Activated</dt>
            <dd>{date(payment.paidAtUtc)}</dd>
          </div>
        )}
      </dl>
    </>
  );
}
